import React, { createContext, useContext, useEffect } from "react";
import { ApplicationWithId } from "../interfaces/IApplications";
import { useApplications } from "../hooks/useApplications";

// Interfejs dla wyniku operacji
interface OperationResult {
  success: boolean;
  error?: string;
  application?: ApplicationWithId;
}

interface ApplicationsContextType {
  // Stan
  applications: ApplicationWithId[];
  isLoading: boolean;

  // Orkiestrowane metody (z hooka)
  handleApplicationAdd: (applicationData: ApplicationWithId) => Promise<OperationResult>;
  handleApplicationUpdate: (
    applicationId: string,
    updatedData: Partial<ApplicationWithId>
  ) => Promise<OperationResult>;
  handleApplicationDelete: (applicationId: string) => Promise<OperationResult>;

  // Metody pomocnicze (zachowane dla kompatybilności)
  refreshApplication: (userId: string, applicationId: string) => void;
}

const ApplicationsContext = createContext<ApplicationsContextType | undefined>(undefined);

export default function ApplicationsProvider({
  children,
  userId,
}: {
  children: React.ReactNode;
  userId: string;
}) {
  // Hook robi całą robotę - DESTRUKTURYZACJA
  const {
    // Stan
    applications,
    isLoading,

    // Orkiestrowane metody
    handleApplicationAdd,
    handleApplicationUpdate,
    handleApplicationDelete,

    // Metody pomocnicze
    refreshApplication,
  } = useApplications(userId);

  // Walidacja spójności: sprawdź czy aplikacje należą do aktualnego użytkownika
  useEffect(() => {
    if (applications.length > 0) {
      const invalidApplications = applications.filter((app) => app.userId !== userId);
      if (invalidApplications.length > 0) {
        console.warn(
          `🚨 [ApplicationsContext] Niekonsystencja danych: znaleziono ${invalidApplications.length} aplikacji należących do innych użytkowników`
        );
        console.warn(
          "Nieprawidłowe aplikacje:",
          invalidApplications.map((app) => ({
            id: app.id,
            userId: app.userId,
            expectedUserId: userId,
          }))
        );
      }
    }
  }, [applications, userId]);

  return (
    <ApplicationsContext.Provider
      value={{
        // Stan
        applications,
        isLoading,

        // Orkiestrowane metody
        handleApplicationAdd,
        handleApplicationUpdate,
        handleApplicationDelete,

        // Metody pomocnicze
        refreshApplication,
      }}
    >
      {children}
    </ApplicationsContext.Provider>
  );
}

export const useApplicationsContext = (): ApplicationsContextType => {
  const context = useContext(ApplicationsContext);
  if (context === undefined) {
    throw new Error(
      "Błąd w ApplicationsContext: useApplications must be used within an ApplicationsProvider"
    );
  }
  return context;
};
